import {useState, useEffect} from 'react';
import {Link, useNavigate} from 'react-router';
import {UilCamera, UilCalendarAlt} from '@iconscout/react-unicons';
import {useAuth} from '../hooks/AuthContext';
import {getHomeSummary} from '../api/AccountApi';
import '../assets/css/target_site.css';
import './Home.css';
// Icons for Menu

// Other assets

// Keyboard activation for the role="button" stat tiles (Enter / Space), matching native buttons.
const activateOnKey = (action) => (event) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    action();
  }
};

export default function Home() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [isFabOpen, setIsFabOpen] = useState(false);

  // State for live API data
  const [homeSummary, setHomeSummary] = useState(null);

  /**
   * Fetch all homepage data on component mount.
   * Uses Promise.allSettled to ensure partial failures don't block the whole page.
   */
  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        const [summaryResult] = await Promise.allSettled([getHomeSummary()]);

        if (!isMounted) return;

        // Extract successful results, fall back to defaults on failure
        if (summaryResult.status === 'fulfilled') {
          setHomeSummary(summaryResult.value);
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Failed to fetch homepage data:', err);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  // Derive display values from API data or auth context
  const points = homeSummary?.points ?? auth.user?.points ?? 0;
  const customerLevel = homeSummary?.customer_level ?? auth.user?.customer_level ?? 'Silver';

  return (
    <>
      {/* Speed Dial Overlay */}
      <div
        className={`${'fab-overlay'} ${isFabOpen ? 'active' : ''}`}
        onClick={() => setIsFabOpen(false)}
      ></div>
      <div
        style={{
          minHeight: '100dvh',
          background: 'linear-gradient(180deg, #FFE5EF 0%, #FFF0F5 18%, #FCF6FA 100%)',
        }}
      >
        <div className="dash-shell" style={{display: 'flex', flexDirection: 'column', gap: 24}}>
          <div style={{paddingTop: 22}}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 16,
              }}
            >
              <img
                src="/assets/logo-round.png"
                alt="AIDentalBook"
                style={{width: 64, height: 64, display: 'block', flexShrink: 0}}
              />
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  paddingTop: 6,
                }}
              >
                <button
                  className="icon-tap"
                  aria-label="Notifications, 3 unread"
                  onClick={() => navigate('/appointments')}
                  style={{
                    position: 'relative',
                    border: 'none',
                    background: 'none',
                    padding: 4,
                    cursor: 'pointer',
                    lineHeight: 0,
                  }}
                >
                  <svg width={24} height={24} viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 3a6 6 0 00-6 6c0 3.1-.7 5-1.4 6.1-.4.6 0 1.4.8 1.4h13.2c.8 0 1.2-.8.8-1.4C18.7 14 18 12.1 18 9a6 6 0 00-6-6z"
                      stroke="#1A1A2E"
                      strokeWidth="1.7"
                      strokeLinejoin="round"
                    />
                    <path d="M10 19.5a2 2 0 004 0" stroke="#1A1A2E" strokeWidth="1.7" strokeLinecap="round" />
                  </svg>
                  <span
                    style={{
                      position: 'absolute',
                      top: '-2px',
                      right: '-3px',
                      minWidth: 18,
                      height: 18,
                      padding: '0 4px',
                      borderRadius: 999,
                      background: '#C43B71',
                      color: '#fff',
                      fontFamily: 'var(--font-manrope), Manrope, system-ui',
                      fontSize: '10.5px',
                      fontWeight: 800,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 6px rgba(196,59,113,0.45)',
                    }}
                  >
                    3
                  </span>
                </button>
                <span
                  className="icon-tap"
                  onClick={() => navigate('/account')}
                  style={{display: 'flex', cursor: 'pointer'}}
                >
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 999,
                      flexShrink: 0,
                      background: 'linear-gradient(135deg, #F8D7E4 0%, #C43B71 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontFamily: 'var(--font-manrope), Manrope, system-ui',
                      fontWeight: 800,
                      fontSize: '15.96px',
                      boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.5)',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src="/assets/john.png"
                      alt=""
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  </div>
                </span>
              </div>
            </div>
            <h1
              className="dash-greet"
              style={{
                fontFamily: 'var(--font-manrope), Manrope, system-ui',
                fontWeight: 800,
                lineHeight: '1.08',
                letterSpacing: '-0.03em',
                color: '#1A1A2E',
                margin: '14px 0 0',
              }}
            >
              Hi, {auth.user ? auth.user.username : 'Gordon'}!
            </h1>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '8px 14px',
                margin: '8px 0 0',
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--font-inter), Inter, system-ui',
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#6B7280',
                }}
              >
                Welcome to your Dental Passport
              </span>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 12px 4px 6px',
                  borderRadius: 999,
                  background: 'rgba(255,255,255,0.8)',
                  border: '1px solid rgba(196, 59, 113, 0.14)',
                  boxShadow: '0 2px 12px rgba(196, 59, 113, 0.06)',
                }}
              >
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 1.5l2.6 2 3.2-.3 1.2 3 2.7 1.8-.7 3.2 1.5 2.9-2.2 2.4.2 3.2-3.1 1-1.6 2.8-3.1-.6-2.7 1.7-2-2.5-3-1.3-.3-3.2L1 14l1.7-2.7-1-3.1 2.4-2.2 1-3 3.2-.2L11 1l1 .5z"
                    fill="#C43B71"
                  />
                  <path
                    d="M8 12l3 3 5-6"
                    stroke="#fff"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span
                  style={{
                    fontFamily: 'var(--font-manrope), Manrope, system-ui',
                    fontSize: '11.5px',
                    fontWeight: 800,
                    letterSpacing: '0.06em',
                    color: '#C43B71',
                    textTransform: 'uppercase',
                  }}
                >
                  {customerLevel.toUpperCase()}
                </span>
                <span
                  style={{
                    fontFamily: 'var(--font-inter), Inter, system-ui',
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#1A1A2E',
                  }}
                >
                  {points} coins
                </span>
              </span>
            </div>
          </div>
          <div
            style={{
              background: 'rgba(255,255,255,0.55)',
              border: '1px solid rgba(196, 59, 113, 0.14)',
              borderRadius: 24,
              boxShadow: '0 8px 28px rgba(26, 26, 46, 0.06)',
              padding: '20px 12px',
            }}
          >
            <div className="dash-features">
              <button
                className="dash-feat-cell"
                aria-label="Images Storage"
                onClick={() => navigate('/oral-photo')}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                }}
              >
                <div className="dash-feat-art" style={{position: 'relative', lineHeight: 0}}>
                  <img
                    className="dash-feat-ico"
                    src="/assets/icon_round_2.png"
                    alt=""
                    style={{display: 'block', borderRadius: '50%'}}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: 19,
                      height: 19,
                      borderRadius: 999,
                      background: 'rgba(255,255,255,0.94)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 1px 4px rgba(26,26,46,0.18)',
                    }}
                  >
                    <svg width={9} height={11} viewBox="0 0 12 14" fill="none">
                      <rect x="1.5" y={6} width={9} height={7} rx="1.5" fill="#C43B71" />
                      <path d="M3.5 6V4a2.5 2.5 0 015 0v2" stroke="#C43B71" strokeWidth="1.4" fill="none" />
                    </svg>
                  </div>
                </div>
                <span
                  className="dash-feat-label"
                  style={{
                    fontFamily: 'var(--font-inter), Inter, system-ui',
                    fontWeight: 600,
                    color: '#1A1A2E',
                    textAlign: 'center',
                    lineHeight: '1.25',
                  }}
                >
                  Images Storage
                </span>
              </button>
              <button
                className="dash-feat-cell"
                aria-label="Appointments"
                onClick={() => navigate('/appointments')}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                }}
              >
                <div className="dash-feat-art" style={{position: 'relative', lineHeight: 0}}>
                  <img
                    className="dash-feat-ico"
                    src="/assets/icon_round_7.png"
                    alt=""
                    style={{display: 'block', borderRadius: '50%'}}
                  />
                </div>
                <span
                  className="dash-feat-label"
                  style={{
                    fontFamily: 'var(--font-inter), Inter, system-ui',
                    fontWeight: 600,
                    color: '#1A1A2E',
                    textAlign: 'center',
                    lineHeight: '1.25',
                  }}
                >
                  Appointments
                </span>
              </button>
              <button
                className="dash-feat-cell"
                aria-label="Dental AI"
                onClick={() => navigate('/dental-ai')}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                }}
              >
                <div className="dash-feat-art" style={{position: 'relative', lineHeight: 0}}>
                  <img
                    className="dash-feat-ico"
                    src="/assets/icon_round_3.png"
                    alt=""
                    style={{display: 'block', borderRadius: '50%'}}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: 19,
                      height: 19,
                      borderRadius: 999,
                      background: 'rgba(255,255,255,0.94)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 1px 4px rgba(26,26,46,0.18)',
                    }}
                  >
                    <svg width={9} height={11} viewBox="0 0 12 14" fill="none">
                      <rect x="1.5" y={6} width={9} height={7} rx="1.5" fill="#C43B71" />
                      <path d="M3.5 6V4a2.5 2.5 0 015 0v2" stroke="#C43B71" strokeWidth="1.4" fill="none" />
                    </svg>
                  </div>
                </div>
                <span
                  className="dash-feat-label"
                  style={{
                    fontFamily: 'var(--font-inter), Inter, system-ui',
                    fontWeight: 600,
                    color: '#1A1A2E',
                    textAlign: 'center',
                    lineHeight: '1.25',
                  }}
                >
                  Dental AI
                </span>
              </button>
              <button
                className="dash-feat-cell"
                aria-label="Records"
                onClick={() => navigate('/dental-records')}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                }}
              >
                <div className="dash-feat-art" style={{position: 'relative', lineHeight: 0}}>
                  <img
                    className="dash-feat-ico"
                    src="/assets/icon_round_6.png"
                    alt=""
                    style={{display: 'block', borderRadius: '50%'}}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: 19,
                      height: 19,
                      borderRadius: 999,
                      background: 'rgba(255,255,255,0.94)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 1px 4px rgba(26,26,46,0.18)',
                    }}
                  >
                    <svg width={9} height={11} viewBox="0 0 12 14" fill="none">
                      <rect x="1.5" y={6} width={9} height={7} rx="1.5" fill="#C43B71" />
                      <path d="M3.5 6V4a2.5 2.5 0 015 0v2" stroke="#C43B71" strokeWidth="1.4" fill="none" />
                    </svg>
                  </div>
                </div>
                <span
                  className="dash-feat-label"
                  style={{
                    fontFamily: 'var(--font-inter), Inter, system-ui',
                    fontWeight: 600,
                    color: '#1A1A2E',
                    textAlign: 'center',
                    lineHeight: '1.25',
                  }}
                >
                  Records
                </span>
              </button>
              <button
                className="dash-feat-cell"
                aria-label="Virtual Consultation"
                onClick={() => navigate('/virtual-consultation')}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                }}
              >
                <div className="dash-feat-art" style={{position: 'relative', lineHeight: 0}}>
                  <img
                    className="dash-feat-ico"
                    src="/assets/icon_round_8.png"
                    alt=""
                    style={{display: 'block', borderRadius: '50%'}}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: 19,
                      height: 19,
                      borderRadius: 999,
                      background: 'rgba(255,255,255,0.94)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 1px 4px rgba(26,26,46,0.18)',
                    }}
                  >
                    <svg width={9} height={11} viewBox="0 0 12 14" fill="none">
                      <rect x="1.5" y={6} width={9} height={7} rx="1.5" fill="#C43B71" />
                      <path d="M3.5 6V4a2.5 2.5 0 015 0v2" stroke="#C43B71" strokeWidth="1.4" fill="none" />
                    </svg>
                  </div>
                </div>
                <span
                  className="dash-feat-label"
                  style={{
                    fontFamily: 'var(--font-inter), Inter, system-ui',
                    fontWeight: 600,
                    color: '#1A1A2E',
                    textAlign: 'center',
                    lineHeight: '1.25',
                  }}
                >
                  Virtual Consultation
                </span>
              </button>
              <button
                className="dash-feat-cell"
                aria-label="Education"
                onClick={() => navigate('/dental-education')}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                }}
              >
                <div className="dash-feat-art" style={{position: 'relative', lineHeight: 0}}>
                  <img
                    className="dash-feat-ico"
                    src="/assets/icon_round_4.png"
                    alt=""
                    style={{display: 'block', borderRadius: '50%'}}
                  />
                </div>
                <span
                  className="dash-feat-label"
                  style={{
                    fontFamily: 'var(--font-inter), Inter, system-ui',
                    fontWeight: 600,
                    color: '#1A1A2E',
                    textAlign: 'center',
                    lineHeight: '1.25',
                  }}
                >
                  Education
                </span>
              </button>
              <button
                className="dash-feat-cell"
                aria-label="Assessments"
                onClick={() => navigate('/assessments')}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                }}
              >
                <div className="dash-feat-art" style={{position: 'relative', lineHeight: 0}}>
                  <img
                    className="dash-feat-ico"
                    src="/assets/icon_round_5.png"
                    alt=""
                    style={{display: 'block', borderRadius: '50%'}}
                  />
                </div>
                <span
                  className="dash-feat-label"
                  style={{
                    fontFamily: 'var(--font-inter), Inter, system-ui',
                    fontWeight: 600,
                    color: '#1A1A2E',
                    textAlign: 'center',
                    lineHeight: '1.25',
                  }}
                >
                  Assessments
                </span>
              </button>
              <button
                className="dash-feat-cell"
                aria-label="Teeth Whitening"
                onClick={() => navigate('/whitening')}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                }}
              >
                <div className="dash-feat-art" style={{position: 'relative', lineHeight: 0}}>
                  <svg className="dash-feat-ico" viewBox="0 0 74 74" fill="none" style={{display: 'block'}}>
                    <circle cx={37} cy={37} r={37} fill="#C4CAEE" />
                    <path
                      d="M37 17.2C34.5 13.4 30.2 12.2 27.2 12.9C21.6 14 17.7 19.2 17.7 25.8C17.7 32.4 19.1 38.6 20.4 44.2C21.5 48.4 22.3 52.6 23 56C23.7 58.8 25 60.4 27.2 60.4C29.5 60.4 31.6 58.2 32.2 54.4C32.8 50.6 33.5 47.5 37 47.5C40.5 47.5 41.2 50.6 41.8 54.4C42.4 58.2 44.5 60.4 46.8 60.4C49 60.4 50.3 58.8 51 56C51.7 52.6 52.5 48.4 53.6 44.2C54.9 38.6 56.3 32.4 56.3 25.8C56.3 19.2 52.4 14 46.8 12.9C43.8 12.2 39.5 13.4 37 17.2Z"
                      fill="#FFFFFF"
                      stroke="#2C3357"
                      strokeWidth="2.4"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M61 13l1.3 3.7 3.7 1.3-3.7 1.3L61 23l-1.3-3.7-3.7-1.3 3.7-1.3L61 13z"
                      fill="#F472A0"
                    />
                  </svg>
                </div>
                <span
                  className="dash-feat-label"
                  style={{
                    fontFamily: 'var(--font-inter), Inter, system-ui',
                    fontWeight: 600,
                    color: '#1A1A2E',
                    textAlign: 'center',
                    lineHeight: '1.25',
                  }}
                >
                  Teeth Whitening
                </span>
              </button>
            </div>
          </div>
          <div className="dash-stats">
            <div
              role="button"
              tabIndex={0}
              className="dash-tap"
              onKeyDown={activateOnKey(() => navigate('/oral-photo'))}
              onClick={() => navigate('/oral-photo')}
              style={{
                background: 'rgba(255,255,255,0.82)',
                backdropFilter: 'blur(20px) saturate(160%)',
                WebkitBackdropFilter: 'blur(20px) saturate(160%)',
                border: '1px solid rgba(196, 59, 113, 0.14)',
                borderRadius: 20,
                boxShadow: '0 8px 28px rgba(26, 26, 46, 0.06)',
                cursor: 'pointer',
                padding: '14px 14px 12px',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <span
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 10,
                    flexShrink: 0,
                    background: 'linear-gradient(135deg, #FFF0F5, #FFE5EF)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg width={17} height={17} viewBox="0 0 24 24" fill="none">
                    <rect x={3} y={4} width={18} height={16} rx={3} stroke="#C43B71" strokeWidth="1.7" />
                    <path
                      d="M12 8c-2 0-3.4.6-3.4 2.2 0 1.2.4 2.4.7 3.6.2.8.5 2 .7 3 .1.8.4 1.2 1 1.2s.7-.6.8-1.4c.1-.9.1-1.4.2-1.4s.1.5.2 1.4c.1.8.2 1.4.8 1.4s.9-.4 1-1.2c.2-1 .5-2.2.7-3 .3-1.2.7-2.4.7-3.6C15.4 8.6 14 8 12 8z"
                      fill="#C43B71"
                    />
                  </svg>
                </span>
                <span
                  style={{
                    fontFamily: 'var(--font-inter), Inter, system-ui',
                    fontSize: '11.5px',
                    fontWeight: 600,
                    color: '#6B7280',
                    lineHeight: '1.2',
                  }}
                >
                  Last X-ray
                </span>
              </div>
              <div>
                <div
                  style={{
                    fontFamily: 'var(--font-manrope), Manrope, system-ui',
                    fontSize: 19,
                    fontWeight: 800,
                    letterSpacing: '-0.025em',
                    lineHeight: '1.15',
                    color: '#1A1A2E',
                  }}
                >
                  Jun 2026
                </div>
                <div
                  style={{
                    fontFamily: 'var(--font-inter), Inter, system-ui',
                    fontSize: 12,
                    fontWeight: 500,
                    color: '#6B7280',
                    marginTop: 2,
                  }}
                >
                  Bitewing
                </div>
              </div>
              <div style={{marginTop: 'auto', paddingTop: 4}}>
                <span
                  style={{
                    border: 'none',
                    background: 'none',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 6,
                    width: '100%',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-manrope), Manrope, system-ui',
                    fontSize: '12.5px',
                    fontWeight: 700,
                    color: '#C43B71',
                  }}
                >
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    View X-rays
                  </span>
                  <span className="link-chevron">
                    <svg width={13} height={13} viewBox="0 0 24 24" fill="none">
                      <path
                        d="M9 5l7 7-7 7"
                        stroke="#C43B71"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </span>
              </div>
            </div>
            <div
              role="button"
              tabIndex={0}
              className="dash-tap"
              onKeyDown={activateOnKey(() => navigate('/dental-records'))}
              onClick={() => navigate('/dental-records')}
              style={{
                background: 'rgba(255,255,255,0.82)',
                backdropFilter: 'blur(20px) saturate(160%)',
                WebkitBackdropFilter: 'blur(20px) saturate(160%)',
                border: '1px solid rgba(196, 59, 113, 0.14)',
                borderRadius: 20,
                boxShadow: '0 8px 28px rgba(26, 26, 46, 0.06)',
                cursor: 'pointer',
                padding: '14px 14px 12px',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <span
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 10,
                    flexShrink: 0,
                    background: 'linear-gradient(135deg, #FFF0F5, #FFE5EF)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg width={17} height={17} viewBox="0 0 24 24" fill="none">
                    <rect x={3} y={5} width={18} height={16} rx={3} stroke="#C43B71" strokeWidth="1.7" />
                    <path
                      d="M8 3v4M16 3v4M3 10h18"
                      stroke="#C43B71"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                    />
                    <path
                      d="M9 15.5l2 2 4-4"
                      stroke="#C43B71"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span
                  style={{
                    fontFamily: 'var(--font-inter), Inter, system-ui',
                    fontSize: '11.5px',
                    fontWeight: 600,
                    color: '#6B7280',
                    lineHeight: '1.2',
                  }}
                >
                  Last Cleaning
                </span>
              </div>
              <div>
                <div
                  style={{
                    fontFamily: 'var(--font-manrope), Manrope, system-ui',
                    fontSize: 19,
                    fontWeight: 800,
                    letterSpacing: '-0.025em',
                    lineHeight: '1.15',
                    color: '#1A1A2E',
                  }}
                >
                  3 Months Ago
                </div>
                <div
                  style={{
                    fontFamily: 'var(--font-inter), Inter, system-ui',
                    fontSize: 12,
                    fontWeight: 500,
                    color: '#6B7280',
                    marginTop: 2,
                  }}
                >
                  May 10, 2026
                </div>
              </div>
              <div style={{marginTop: 'auto', paddingTop: 4}}>
                <span
                  style={{
                    border: 'none',
                    background: 'none',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 6,
                    width: '100%',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-manrope), Manrope, system-ui',
                    fontSize: '12.5px',
                    fontWeight: 700,
                    color: '#C43B71',
                  }}
                >
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    View History
                  </span>
                  <span className="link-chevron">
                    <svg width={13} height={13} viewBox="0 0 24 24" fill="none">
                      <path
                        d="M9 5l7 7-7 7"
                        stroke="#C43B71"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </span>
              </div>
            </div>
            <div
              role="button"
              tabIndex={0}
              className="dash-tap"
              onKeyDown={activateOnKey(() => navigate('/appointments'))}
              onClick={() => navigate('/appointments')}
              style={{
                background: 'rgba(255,255,255,0.82)',
                backdropFilter: 'blur(20px) saturate(160%)',
                WebkitBackdropFilter: 'blur(20px) saturate(160%)',
                border: '1px solid rgba(196, 59, 113, 0.14)',
                borderRadius: 20,
                boxShadow: '0 8px 28px rgba(26, 26, 46, 0.06)',
                cursor: 'pointer',
                padding: '14px 14px 12px',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <span
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 10,
                    flexShrink: 0,
                    background: 'linear-gradient(135deg, #FFF0F5, #FFE5EF)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg width={17} height={17} viewBox="0 0 24 24" fill="none">
                    <rect x={3} y={5} width={18} height={16} rx={3} stroke="#C43B71" strokeWidth="1.7" />
                    <path
                      d="M8 3v4M16 3v4M3 10h18"
                      stroke="#C43B71"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                    />
                    <rect x={7} y={13} width={4} height={4} rx={1} fill="#C43B71" />
                  </svg>
                </span>
                <span
                  style={{
                    fontFamily: 'var(--font-inter), Inter, system-ui',
                    fontSize: '11.5px',
                    fontWeight: 600,
                    color: '#6B7280',
                    lineHeight: '1.2',
                  }}
                >
                  Next Appointment
                </span>
              </div>
              <div>
                <div
                  style={{
                    fontFamily: 'var(--font-manrope), Manrope, system-ui',
                    fontSize: 19,
                    fontWeight: 800,
                    letterSpacing: '-0.025em',
                    lineHeight: '1.15',
                    color: '#1A1A2E',
                  }}
                >
                  Aug 18, 2026
                </div>
                <div
                  style={{
                    fontFamily: 'var(--font-inter), Inter, system-ui',
                    fontSize: 12,
                    fontWeight: 500,
                    color: '#6B7280',
                    marginTop: 2,
                  }}
                >
                  10:00 AM
                </div>
              </div>
              <div style={{marginTop: 'auto', paddingTop: 4}}>
                <span
                  style={{
                    border: 'none',
                    background: 'none',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 6,
                    width: '100%',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-manrope), Manrope, system-ui',
                    fontSize: '12.5px',
                    fontWeight: 700,
                    color: '#C43B71',
                  }}
                >
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    View Appointment
                  </span>
                  <span className="link-chevron">
                    <svg width={13} height={13} viewBox="0 0 24 24" fill="none">
                      <path
                        d="M9 5l7 7-7 7"
                        stroke="#C43B71"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </span>
              </div>
            </div>
            <div
              role="button"
              tabIndex={0}
              className="dash-tap"
              onKeyDown={activateOnKey(() => navigate('/account'))}
              onClick={() => navigate('/account')}
              style={{
                background: 'rgba(255,255,255,0.82)',
                backdropFilter: 'blur(20px) saturate(160%)',
                WebkitBackdropFilter: 'blur(20px) saturate(160%)',
                border: '1px solid rgba(196, 59, 113, 0.14)',
                borderRadius: 20,
                boxShadow: '0 8px 28px rgba(26, 26, 46, 0.06)',
                cursor: 'pointer',
                padding: '14px 14px 12px',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <span
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 10,
                    flexShrink: 0,
                    background: 'linear-gradient(135deg, #FFF0F5, #FFE5EF)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg width={17} height={17} viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 2.5l7.5 3v6c0 4.6-3.1 8.6-7.5 10-4.4-1.4-7.5-5.4-7.5-10v-6l7.5-3z"
                      stroke="#C43B71"
                      strokeWidth="1.7"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8.8 11.8l2.2 2.2 4.2-4.4"
                      stroke="#C43B71"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span
                  style={{
                    fontFamily: 'var(--font-inter), Inter, system-ui',
                    fontSize: '11.5px',
                    fontWeight: 600,
                    color: '#6B7280',
                    lineHeight: '1.2',
                  }}
                >
                  Insurance Status
                </span>
              </div>
              <div>
                <div
                  style={{
                    fontFamily: 'var(--font-manrope), Manrope, system-ui',
                    fontSize: 19,
                    fontWeight: 800,
                    letterSpacing: '-0.025em',
                    lineHeight: '1.15',
                    color: '#C43B71',
                  }}
                >
                  Active
                </div>
                <div
                  style={{
                    fontFamily: 'var(--font-inter), Inter, system-ui',
                    fontSize: 12,
                    fontWeight: 500,
                    color: '#6B7280',
                    marginTop: 2,
                  }}
                >
                  Delta Dental PPO
                </div>
              </div>
              <div style={{marginTop: 'auto', paddingTop: 4}}>
                <span
                  style={{
                    border: 'none',
                    background: 'none',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 6,
                    width: '100%',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-manrope), Manrope, system-ui',
                    fontSize: '12.5px',
                    fontWeight: 700,
                    color: '#C43B71',
                  }}
                >
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    View Details
                  </span>
                  <span className="link-chevron">
                    <svg width={13} height={13} viewBox="0 0 24 24" fill="none">
                      <path
                        d="M9 5l7 7-7 7"
                        stroke="#C43B71"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </span>
              </div>
            </div>
          </div>
          <div className="dash-pair">
            <section>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                <h3
                  style={{
                    fontFamily: 'var(--font-manrope), Manrope, system-ui',
                    fontSize: 17,
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    color: '#1A1A2E',
                    margin: 0,
                  }}
                >
                  Family Hub
                </h3>
                <button
                  className="link-action"
                  onClick={() => navigate('/account')}
                  style={{
                    border: 'none',
                    background: 'none',
                    padding: 0,
                    fontFamily: 'var(--font-manrope), Manrope, system-ui',
                    fontSize: 13,
                    fontWeight: 700,
                    color: '#C43B71',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  View All
                </button>
              </div>
              <div
                style={{
                  background: 'rgba(255,255,255,0.82)',
                  backdropFilter: 'blur(20px) saturate(160%)',
                  WebkitBackdropFilter: 'blur(20px) saturate(160%)',
                  border: '1px solid rgba(196, 59, 113, 0.14)',
                  borderRadius: 20,
                  boxShadow: '0 8px 28px rgba(26, 26, 46, 0.06)',
                  padding: '6px 14px',
                }}
              >
                <div
                  className="dash-row"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '11px 8px',
                    margin: '0 -8px',
                    borderTop: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <span className="dash-row-art" style={{display: 'flex'}}>
                    <div
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 999,
                        flexShrink: 0,
                        background: '#E4DCEF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#1A1A2E',
                        fontFamily: 'var(--font-manrope), Manrope, system-ui',
                        fontWeight: 800,
                        fontSize: '14.44px',
                        boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.5)',
                        overflow: 'hidden',
                      }}
                    >
                      <img
                        src="/assets/grandpa.png"
                        alt=""
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    </div>
                  </span>
                  <div style={{minWidth: 0}}>
                    <div
                      style={{
                        fontFamily: 'var(--font-manrope), Manrope, system-ui',
                        fontSize: 14,
                        fontWeight: 700,
                        color: '#1A1A2E',
                        lineHeight: '1.2',
                      }}
                    >
                      GrandPa
                    </div>
                    <div
                      style={{
                        fontFamily: 'var(--font-inter), Inter, system-ui',
                        fontSize: '11.5px',
                        color: '#6B7280',
                        marginTop: 1,
                      }}
                    >
                      Grandfather
                    </div>
                  </div>
                </div>
                <div
                  className="dash-row"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '11px 8px',
                    margin: '0 -8px',
                    borderTop: '1px solid rgba(196, 59, 113, 0.14)',
                    cursor: 'pointer',
                  }}
                >
                  <span className="dash-row-art" style={{display: 'flex'}}>
                    <div
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 999,
                        flexShrink: 0,
                        background: '#F8D7E4',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#1A1A2E',
                        fontFamily: 'var(--font-manrope), Manrope, system-ui',
                        fontWeight: 800,
                        fontSize: '14.44px',
                        boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.5)',
                        overflow: 'hidden',
                      }}
                    >
                      <img
                        src="/assets/joyce.png"
                        alt=""
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    </div>
                  </span>
                  <div style={{minWidth: 0}}>
                    <div
                      style={{
                        fontFamily: 'var(--font-manrope), Manrope, system-ui',
                        fontSize: 14,
                        fontWeight: 700,
                        color: '#1A1A2E',
                        lineHeight: '1.2',
                      }}
                    >
                      Joyce
                    </div>
                    <div
                      style={{
                        fontFamily: 'var(--font-inter), Inter, system-ui',
                        fontSize: '11.5px',
                        color: '#6B7280',
                        marginTop: 1,
                      }}
                    >
                      Daughter
                    </div>
                  </div>
                </div>
                <div
                  className="dash-row"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '11px 8px',
                    margin: '0 -8px',
                    borderTop: '1px solid rgba(196, 59, 113, 0.14)',
                    cursor: 'pointer',
                  }}
                >
                  <span className="dash-row-art" style={{display: 'flex'}}>
                    <div
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 999,
                        flexShrink: 0,
                        background: '#D3E5F5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#1A1A2E',
                        fontFamily: 'var(--font-manrope), Manrope, system-ui',
                        fontWeight: 800,
                        fontSize: '14.44px',
                        boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.5)',
                        overflow: 'hidden',
                      }}
                    >
                      <img
                        src="/assets/andrew.png"
                        alt=""
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    </div>
                  </span>
                  <div style={{minWidth: 0}}>
                    <div
                      style={{
                        fontFamily: 'var(--font-manrope), Manrope, system-ui',
                        fontSize: 14,
                        fontWeight: 700,
                        color: '#1A1A2E',
                        lineHeight: '1.2',
                      }}
                    >
                      Andrew
                    </div>
                    <div
                      style={{
                        fontFamily: 'var(--font-inter), Inter, system-ui',
                        fontSize: '11.5px',
                        color: '#6B7280',
                        marginTop: 1,
                      }}
                    >
                      Son
                    </div>
                  </div>
                </div>
                <div
                  className="dash-row"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '11px 8px',
                    margin: '0 -8px',
                    borderTop: '1px solid rgba(196, 59, 113, 0.14)',
                    cursor: 'pointer',
                  }}
                >
                  <span className="dash-row-art" style={{display: 'flex'}}>
                    <div
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 999,
                        flexShrink: 0,
                        background: '#F3E3CE',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#1A1A2E',
                        fontFamily: 'var(--font-manrope), Manrope, system-ui',
                        fontWeight: 800,
                        fontSize: '14.44px',
                        boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.5)',
                        overflow: 'hidden',
                      }}
                    >
                      <img
                        src="/assets/frankie.png"
                        alt=""
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    </div>
                  </span>
                  <div style={{minWidth: 0}}>
                    <div
                      style={{
                        fontFamily: 'var(--font-manrope), Manrope, system-ui',
                        fontSize: 14,
                        fontWeight: 700,
                        color: '#1A1A2E',
                        lineHeight: '1.2',
                      }}
                    >
                      Frankie
                    </div>
                    <div
                      style={{
                        fontFamily: 'var(--font-inter), Inter, system-ui',
                        fontSize: '11.5px',
                        color: '#6B7280',
                        marginTop: 1,
                      }}
                    >
                      Pet
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <section>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                <h3
                  style={{
                    fontFamily: 'var(--font-manrope), Manrope, system-ui',
                    fontSize: 17,
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    color: '#1A1A2E',
                    margin: 0,
                  }}
                >
                  Recently Added
                </h3>
                <button
                  className="link-action"
                  onClick={() => navigate('/dental-records')}
                  style={{
                    border: 'none',
                    background: 'none',
                    padding: 0,
                    fontFamily: 'var(--font-manrope), Manrope, system-ui',
                    fontSize: 13,
                    fontWeight: 700,
                    color: '#C43B71',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  View All
                </button>
              </div>
              <div
                style={{
                  background: 'rgba(255,255,255,0.82)',
                  backdropFilter: 'blur(20px) saturate(160%)',
                  WebkitBackdropFilter: 'blur(20px) saturate(160%)',
                  border: '1px solid rgba(196, 59, 113, 0.14)',
                  borderRadius: 20,
                  boxShadow: '0 8px 28px rgba(26, 26, 46, 0.06)',
                  padding: '6px 14px',
                }}
              >
                <div
                  className="dash-row"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '11px 8px',
                    margin: '0 -8px',
                    borderTop: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <div
                    className="dash-row-art"
                    style={{
                      width: 54,
                      height: 42,
                      borderRadius: 10,
                      flexShrink: 0,
                      background: '#2A2E45',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.18)',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src="/assets/bitewing-x-ray.png"
                      alt=""
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  </div>
                  <div style={{minWidth: 0, flex: 1}}>
                    <div
                      style={{
                        fontFamily: 'var(--font-manrope), Manrope, system-ui',
                        fontSize: '13.5px',
                        fontWeight: 700,
                        color: '#1A1A2E',
                        lineHeight: '1.2',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Bitewing X-ray
                    </div>
                    <div
                      style={{
                        fontFamily: 'var(--font-inter), Inter, system-ui',
                        fontSize: '11.5px',
                        color: '#6B7280',
                        marginTop: 2,
                      }}
                    >
                      Jun 12, 2026
                    </div>
                  </div>
                  <span
                    style={{
                      flexShrink: 0,
                      padding: '3px 10px',
                      borderRadius: 999,
                      background: 'rgba(196,59,113,0.09)',
                      fontFamily: 'var(--font-manrope), Manrope, system-ui',
                      fontSize: '10.5px',
                      fontWeight: 700,
                      color: '#C43B71',
                    }}
                  >
                    X-ray
                  </span>
                </div>
                <div
                  className="dash-row"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '11px 8px',
                    margin: '0 -8px',
                    borderTop: '1px solid rgba(196, 59, 113, 0.14)',
                    cursor: 'pointer',
                  }}
                >
                  <div
                    className="dash-row-art"
                    style={{
                      width: 54,
                      height: 42,
                      borderRadius: 10,
                      flexShrink: 0,
                      background: '#E8B9C6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.18)',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src="/assets/intraoral-scan.png"
                      alt=""
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  </div>
                  <div style={{minWidth: 0, flex: 1}}>
                    <div
                      style={{
                        fontFamily: 'var(--font-manrope), Manrope, system-ui',
                        fontSize: '13.5px',
                        fontWeight: 700,
                        color: '#1A1A2E',
                        lineHeight: '1.2',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Intraoral Scan
                    </div>
                    <div
                      style={{
                        fontFamily: 'var(--font-inter), Inter, system-ui',
                        fontSize: '11.5px',
                        color: '#6B7280',
                        marginTop: 2,
                      }}
                    >
                      Jun 12, 2026
                    </div>
                  </div>
                  <span
                    style={{
                      flexShrink: 0,
                      padding: '3px 10px',
                      borderRadius: 999,
                      background: 'rgba(196,59,113,0.09)',
                      fontFamily: 'var(--font-manrope), Manrope, system-ui',
                      fontSize: '10.5px',
                      fontWeight: 700,
                      color: '#C43B71',
                    }}
                  >
                    Scan
                  </span>
                </div>
                <div
                  className="dash-row"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '11px 8px',
                    margin: '0 -8px',
                    borderTop: '1px solid rgba(196, 59, 113, 0.14)',
                    cursor: 'pointer',
                  }}
                >
                  <div
                    className="dash-row-art"
                    style={{
                      width: 54,
                      height: 42,
                      borderRadius: 10,
                      flexShrink: 0,
                      background: '#3A3550',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.18)',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src="/assets/cbct-3d.png"
                      alt=""
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  </div>
                  <div style={{minWidth: 0, flex: 1}}>
                    <div
                      style={{
                        fontFamily: 'var(--font-manrope), Manrope, system-ui',
                        fontSize: '13.5px',
                        fontWeight: 700,
                        color: '#1A1A2E',
                        lineHeight: '1.2',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      CBCT 3D
                    </div>
                    <div
                      style={{
                        fontFamily: 'var(--font-inter), Inter, system-ui',
                        fontSize: '11.5px',
                        color: '#6B7280',
                        marginTop: 2,
                      }}
                    >
                      Jun 10, 2026
                    </div>
                  </div>
                  <span
                    style={{
                      flexShrink: 0,
                      padding: '3px 10px',
                      borderRadius: 999,
                      background: 'rgba(196,59,113,0.09)',
                      fontFamily: 'var(--font-manrope), Manrope, system-ui',
                      fontSize: '10.5px',
                      fontWeight: 700,
                      color: '#C43B71',
                    }}
                  >
                    3D
                  </span>
                </div>
              </div>
            </section>
          </div>
          <div className="dash-pair">
            <section>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                <h3
                  style={{
                    fontFamily: 'var(--font-manrope), Manrope, system-ui',
                    fontSize: 17,
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    color: '#1A1A2E',
                    margin: 0,
                  }}
                >
                  Quick Actions
                </h3>
              </div>
              <div style={{display: 'flex', gap: 12}}>
                <div
                  role="button"
                  tabIndex={0}
                  className="dash-tap"
                  onClick={() => navigate('/oral-photo')}
                  style={{
                    background: 'rgba(255,255,255,0.82)',
                    backdropFilter: 'blur(20px) saturate(160%)',
                    WebkitBackdropFilter: 'blur(20px) saturate(160%)',
                    border: '1px solid rgba(196, 59, 113, 0.14)',
                    borderRadius: 20,
                    boxShadow: '0 8px 28px rgba(26, 26, 46, 0.06)',
                    cursor: 'pointer',
                    flex: 1,
                    padding: '18px 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <span
                    className="dash-quick-art"
                    style={{
                      width: 58,
                      height: 58,
                      borderRadius: 999,
                      overflow: 'hidden',
                      background: 'linear-gradient(135deg, #FFF0F5, #F5F2FF)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src="/assets/3d-model.png"
                      alt=""
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--font-manrope), Manrope, system-ui',
                      fontSize: '13.5px',
                      fontWeight: 700,
                      color: '#1A1A2E',
                    }}
                  >
                    3D Model
                  </span>
                </div>
                <div
                  role="button"
                  tabIndex={0}
                  className="dash-tap"
                  onClick={() => navigate('/oral-supplier')}
                  style={{
                    background: 'rgba(255,255,255,0.82)',
                    backdropFilter: 'blur(20px) saturate(160%)',
                    WebkitBackdropFilter: 'blur(20px) saturate(160%)',
                    border: '1px solid rgba(196, 59, 113, 0.14)',
                    borderRadius: 20,
                    boxShadow: '0 8px 28px rgba(26, 26, 46, 0.06)',
                    cursor: 'pointer',
                    flex: 1,
                    padding: '18px 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <span
                    className="dash-quick-art"
                    style={{
                      width: 58,
                      height: 58,
                      borderRadius: 999,
                      overflow: 'hidden',
                      background: 'linear-gradient(135deg, #FFF0F5, #F5F2FF)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src="/assets/e-mart.png"
                      alt=""
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--font-manrope), Manrope, system-ui',
                      fontSize: '13.5px',
                      fontWeight: 700,
                      color: '#1A1A2E',
                    }}
                  >
                    eMart
                  </span>
                </div>
              </div>
            </section>
            <section>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                <h3
                  style={{
                    fontFamily: 'var(--font-manrope), Manrope, system-ui',
                    fontSize: 17,
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    color: '#1A1A2E',
                    margin: 0,
                  }}
                >
                  Messages from Your Dental Office
                </h3>
                <button
                  className="link-action"
                  onClick={() => navigate('/appointments')}
                  style={{
                    border: 'none',
                    background: 'none',
                    padding: 0,
                    fontFamily: 'var(--font-manrope), Manrope, system-ui',
                    fontSize: 13,
                    fontWeight: 700,
                    color: '#C43B71',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  View All
                </button>
              </div>
              <div
                style={{
                  background: 'rgba(255,255,255,0.82)',
                  backdropFilter: 'blur(20px) saturate(160%)',
                  WebkitBackdropFilter: 'blur(20px) saturate(160%)',
                  border: '1px solid rgba(196, 59, 113, 0.14)',
                  borderRadius: 20,
                  boxShadow: '0 8px 28px rgba(26, 26, 46, 0.06)',
                  padding: 14,
                }}
              >
                <div style={{display: 'flex', gap: 12}}>
                  <div
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 999,
                      flexShrink: 0,
                      background: '#D9E7F5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#1A1A2E',
                      fontFamily: 'var(--font-manrope), Manrope, system-ui',
                      fontWeight: 800,
                      fontSize: '14.44px',
                      boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.5)',
                      overflow: 'hidden',
                    }}
                  >
                    <img
                      src="/assets/office.png"
                      alt=""
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  </div>
                  <div style={{minWidth: 0, flex: 1}}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'space-between',
                        gap: 8,
                      }}
                    >
                      <span
                        style={{
                          fontFamily: 'var(--font-manrope), Manrope, system-ui',
                          fontSize: '13.5px',
                          fontWeight: 700,
                          color: '#1A1A2E',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Agape Dental Group
                      </span>
                      <span
                        style={{
                          fontFamily: 'var(--font-inter), Inter, system-ui',
                          fontSize: 11,
                          color: '#6B7280',
                          flexShrink: 0,
                        }}
                      >
                        2h ago
                      </span>
                    </div>
                    <p
                      style={{
                        fontFamily: 'var(--font-inter), Inter, system-ui',
                        fontSize: '12.5px',
                        lineHeight: '1.55',
                        color: '#6B7280',
                        margin: '5px 0 0',
                      }}
                    >
                      Hi John! Your next appointment is coming up on Aug 18 at 10:00 AM. We look forward to
                      seeing you!
                    </p>
                  </div>
                </div>
                <button
                  className="btn-press"
                  onClick={() => navigate('/appointments')}
                  style={{
                    marginTop: 14,
                    width: '100%',
                    height: 42,
                    borderRadius: 999,
                    border: 'none',
                    background: 'linear-gradient(135deg, #F472A0 0%, #C43B71 100%)',
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    fontFamily: 'var(--font-manrope), Manrope, system-ui',
                    fontSize: '13.5px',
                    fontWeight: 700,
                    boxShadow: '0 8px 18px rgba(196,59,113,0.28)',
                  }}
                >
                  <svg width={17} height={17} viewBox="0 0 24 24" fill="none">
                    <path
                      d="M4 5h16a1.5 1.5 0 011.5 1.5v9A1.5 1.5 0 0120 17h-7l-4.5 3.5V17H4a1.5 1.5 0 01-1.5-1.5v-9A1.5 1.5 0 014 5z"
                      stroke="#fff"
                      strokeWidth="1.7"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Message Us
                </button>
              </div>
            </section>
          </div>
        </div>
        <div
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 50,
            paddingTop: 10,
            paddingBottom: 'max(18px, env(safe-area-inset-bottom))',
            paddingLeft: 14,
            paddingRight: 14,
            background: 'rgba(255,255,255,0.86)',
            backdropFilter: 'blur(28px) saturate(180%)',
            WebkitBackdropFilter: 'blur(28px) saturate(180%)',
            boxShadow: '0 -8px 28px rgba(26,26,46,0.07)',
          }}
        >
          <div className="dash-nav-rail">
            <button
              className="dash-nav-tab"
              aria-current="page"
              onClick={() => navigate('/')}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                background: 'none',
                border: 'none',
                padding: '0 6px',
                cursor: 'pointer',
                flex: 1,
                minWidth: 0,
              }}
            >
              <span
                style={{
                  width: 34,
                  height: 34,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span
                  style={{
                    width: 33,
                    height: 33,
                    backgroundColor: '#C43B71',
                    WebkitMaskImage: 'url(/assets/nav_ico_filled_1.png)',
                    maskImage: 'url(/assets/nav_ico_filled_1.png)',
                    WebkitMaskSize: 'contain',
                    maskSize: 'contain',
                    WebkitMaskRepeat: 'no-repeat',
                    maskRepeat: 'no-repeat',
                    WebkitMaskPosition: 'center',
                    maskPosition: 'center',
                    transition: 'background-color 200ms ease',
                  }}
                />
              </span>
              <span
                style={{
                  fontFamily: 'var(--font-inter), Inter, system-ui',
                  fontSize: '10.5px',
                  fontWeight: 700,
                  color: '#C43B71',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
              >
                Home
              </span>
            </button>
            <button
              className="dash-nav-tab"
              onClick={() => navigate('/dental-records')}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                background: 'none',
                border: 'none',
                padding: '0 6px',
                cursor: 'pointer',
                flex: 1,
                minWidth: 0,
              }}
            >
              <span
                style={{
                  width: 34,
                  height: 34,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span
                  style={{
                    width: 33,
                    height: 33,
                    backgroundColor: '#9CA3AF',
                    WebkitMaskImage: 'url(/assets/nav_ico_outlined_4.png)',
                    maskImage: 'url(/assets/nav_ico_outlined_4.png)',
                    WebkitMaskSize: 'contain',
                    maskSize: 'contain',
                    WebkitMaskRepeat: 'no-repeat',
                    maskRepeat: 'no-repeat',
                    WebkitMaskPosition: 'center',
                    maskPosition: 'center',
                    transition: 'background-color 200ms ease',
                  }}
                />
              </span>
              <span
                style={{
                  fontFamily: 'var(--font-inter), Inter, system-ui',
                  fontSize: '10.5px',
                  fontWeight: 500,
                  color: '#6B7280',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
              >
                Records
              </span>
            </button>

            <div className={`dash-fab-container ${isFabOpen ? 'active' : ''}`}>
              <button
                type="button"
                className={`dash-fab ${isFabOpen ? 'active' : ''}`}
                onClick={() => setIsFabOpen(!isFabOpen)}
                aria-label={isFabOpen ? 'Close quick actions' : 'Open quick actions'}
                title={isFabOpen ? 'Close quick actions' : 'Open quick actions'}
                aria-expanded={isFabOpen}
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 999,
                  border: 'none',
                  flexShrink: 0,
                  background: 'linear-gradient(135deg, #F472A0 0%, #C43B71 100%)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 10px 24px rgba(196,59,113,0.42)',
                  marginBottom: 6,
                  transform: isFabOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                  transition: 'transform 220ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                }}
              >
                <svg width={26} height={26} viewBox="0 0 24 24" fill="none">
                  <path d="M12 5v14M5 12h14" stroke="#fff" strokeWidth={2.4} strokeLinecap="round" />
                </svg>
              </button>

              <div className={'drop-up-menu'}>
                <Link
                  to="/oral-photo/add"
                  className={'drop-up-item'}
                  style={{transitionDelay: '0.1s'}}
                  onClick={() => setIsFabOpen(false)}
                >
                  <div className={'drop-up-item-icon'}>
                    <UilCamera size="18" color="#c43b71" />
                  </div>
                  Add Image
                </Link>
                <Link
                  to="/appointments/add"
                  className={'drop-up-item'}
                  style={{transitionDelay: '0.05s'}}
                  onClick={() => setIsFabOpen(false)}
                >
                  <div className={'drop-up-item-icon'}>
                    <UilCalendarAlt size="18" color="#c43b71" />
                  </div>
                  Book Appt
                </Link>
              </div>
            </div>

            <button
              className="dash-nav-tab"
              onClick={() => navigate('/dental-ai')}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                background: 'none',
                border: 'none',
                padding: '0 6px',
                cursor: 'pointer',
                flex: 1,
                minWidth: 0,
              }}
            >
              <span
                style={{
                  width: 34,
                  height: 34,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span
                  style={{
                    width: 33,
                    height: 33,
                    backgroundColor: '#9CA3AF',
                    WebkitMaskImage: 'url(/assets/nav_ico_outlined_2.png)',
                    maskImage: 'url(/assets/nav_ico_outlined_2.png)',
                    WebkitMaskSize: 'contain',
                    maskSize: 'contain',
                    WebkitMaskRepeat: 'no-repeat',
                    maskRepeat: 'no-repeat',
                    WebkitMaskPosition: 'center',
                    maskPosition: 'center',
                    transition: 'background-color 200ms ease',
                  }}
                />
              </span>
              <span
                style={{
                  fontFamily: 'var(--font-inter), Inter, system-ui',
                  fontSize: '10.5px',
                  fontWeight: 500,
                  color: '#6B7280',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
              >
                AI Assistant
              </span>
            </button>
            <button
              className="dash-nav-tab"
              onClick={() => navigate('/account')}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                background: 'none',
                border: 'none',
                padding: '0 6px',
                cursor: 'pointer',
                flex: 1,
                minWidth: 0,
              }}
            >
              <span
                style={{
                  width: 34,
                  height: 34,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width={33} height={33} viewBox="0 0 24 24" fill="none">
                  <circle cx={12} cy={8} r="3.6" stroke="#9CA3AF" strokeWidth="1.7" />
                  <path
                    d="M4.5 20a7.5 7.5 0 0115 0"
                    stroke="#9CA3AF"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <span
                style={{
                  fontFamily: 'var(--font-inter), Inter, system-ui',
                  fontSize: '10.5px',
                  fontWeight: 500,
                  color: '#6B7280',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
              >
                Profile
              </span>
            </button>
          </div>
        </div>
      </div>
      {/*$*/}
    </>
  );
}
